import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { DreamComponentsProvider, embedSubdomain } from '@shared/dream-components';

import { getAppGlobals } from '~/components/AppGlobals/utils';
import DefaultPremiumGate from '~/components/DefaultPremiumGate';
import GlobalElementsSerializer from '~/components/DreamBuilder/GlobalElementsSerializer';
import { lazyBlock } from '~/components/DreamBuilder/lazyBlock';
import SearchModal from '~/components/DreamBuilder/nodes/SearchModal/SearchModal';
import usePremiumPageGate from '~/hooks/usePremiumPageGate';
import type { CodePageData, Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';

const CodePage = lazyBlock('code-page', () => import('.').then((module) => module.default));

type Props = PropsWithChildren<{
  site: Site;
  page: Page & { code: CodePageData };
  isSearchModalEnabled?: boolean;
}>;

// StaticPage for code pages. The publisher's code draws its own navbar and footer.
const CodeStaticPage = ({ site, page, children, isSearchModalEnabled = true }: Props) => {
  const { isPremiumGated, hasPremiumAccess, isFetchingSubscription, subscription } = usePremiumPageGate(page, site);
  const { i18n } = useTranslation();

  const externalEmbedBaseDomain = getAppGlobals().VITE_EXTERNAL_EMBED_DOMAIN || '';
  if (import.meta.env.PROD && externalEmbedBaseDomain === '') {
    throw new Error('VITE_EXTERNAL_EMBED_DOMAIN should have a valid domain');
  }
  const externalEmbedUrl = externalEmbedBaseDomain
    ? `https://${embedSubdomain(new URL(site.publication.url).hostname)}.${externalEmbedBaseDomain}`
    : '';

  const renderPageContent = () => {
    if (isPremiumGated && isFetchingSubscription) {
      return null;
    }

    if (isPremiumGated && !hasPremiumAccess && page.paywall) {
      return (
        <DefaultPremiumGate paywall={page.paywall} subscription={subscription} primaryTheme={site.primary_theme} />
      );
    }

    return (
      <>
        <CodePage key={page.id} page={page} site={site} />
        {children}
      </>
    );
  };

  return (
    <DreamComponentsProvider
      context={{
        responsiveImagesEnabled: true,
        imagePriorityThreshold: 5,
        imageHighPriorityCount: 1,
        publicationTimeZone: site.publication.time_zone,
        externalEmbedEnabled: site.publication.has_external_embed,
        externalEmbedUrl,
        language: i18n.language,
        adsensePublisherId: site.publication.has_adsense ? (site.adsense_publisher_id ?? undefined) : undefined,
        rawHtmlPage: page.viewable_page_version?.uses_raw_html ?? false,
      }}
    >
      {renderPageContent()}
      <GlobalElementsSerializer page={page} site={site} />
      {isSearchModalEnabled && <SearchModal />}
    </DreamComponentsProvider>
  );
};

export default CodeStaticPage;
